import React, { useCallback, useEffect, useState } from "react";
import { api, errMsg, formatDate, formatCurrency } from "../lib/api";
import { Download, FileText, History } from "lucide-react";
import { toast } from "sonner";

export default function StatusPackHistory({ projectId, refreshKey }) {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/status-packs`);
      setPacks(data);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  }, [projectId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const download = async (pack) => {
    try {
      const { data } = await api.get(`/projects/${projectId}/status-packs/${pack.id}`);
      const blob = new Blob([data.html], { type: "text/html" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${pack.subject.replace(/[^a-z0-9]+/gi, "_")}.html`;
      a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const preview = async (pack) => {
    try {
      const { data } = await api.get(`/projects/${projectId}/status-packs/${pack.id}`);
      // Open the archived HTML in a sandboxed blob URL tab (never injected into this page).
      const blob = new Blob([data.html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.error("Pop-up blocked — allow pop-ups to preview the pack");
      // Revoke after the new tab has had time to load the blob
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mt-5 border-t border-slate-800 pt-4" data-testid="status-pack-history">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 mb-3">
        <History size={12}/> Archive
      </div>
      {loading ? <div className="text-slate-500 text-sm">Loading…</div>
        : packs.length === 0 ? <div className="text-slate-500 text-sm">No status packs yet. Hit "Send test now" to create one.</div>
        : <div className="space-y-2">
            {packs.map((p) => {
              const m = p.metrics || {};
              const tone = m.risk_tier === "high" ? "text-rose-300" : m.risk_tier === "medium" ? "text-amber-300" : "text-emerald-300";
              return (
                <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/40 border border-slate-800" data-testid={`pack-row-${p.id}`}>
                  <div className="flex-1">
                    <div className="text-sm text-white font-medium">{formatDate(p.created_at)}</div>
                    <div className="text-[11px] text-slate-500">
                      {p.recipients?.length} recipient{p.recipients?.length === 1 ? "" : "s"} •
                      Delivery {m.calculated_delivery || "—"} •
                      Est {formatCurrency(m.estimated_cost || 0)} •
                      <span className={`capitalize ${tone}`}> Risk {m.risk_score ?? "—"} {m.risk_tier}</span>
                    </div>
                  </div>
                  <button onClick={() => preview(p)} className="text-xs text-blue-300 hover:text-blue-200 inline-flex items-center gap-1 border border-slate-700 rounded px-2 py-1" data-testid={`preview-pack-${p.id}`}><FileText size={12}/> Preview</button>
                  <button onClick={() => download(p)} className="text-xs text-blue-300 hover:text-blue-200 inline-flex items-center gap-1 border border-slate-700 rounded px-2 py-1" data-testid={`download-pack-${p.id}`}><Download size={12}/> HTML</button>
                </div>
              );
            })}
          </div>}
    </div>
  );
}
